// Diário › Cartas para o Futuro (0.65): escreva hoje (página infinita, fotos e links) e escolha quando abre.
// Ao salvar, a carta fica TRANCADA até a data de abrir. No dia, chega o aviso e o dia fica marcado no My Planner
// ("💌 Abrir carta para o futuro", só com o título). Depois de aberta, pode ser lida, mas não editada.
import { useState } from 'react';
import { Alert, Linking, Pressable, Text, View } from 'react-native';
import { getDoc, useDoc } from '../../store/store';
import { gravar, ler, mudarDiario, useDiario, type Carta } from '../../store/diario';
import { newTask, saveTask } from '../../store/planner';
import { useAuth, semBloqueio } from '../../auth/AuthContext';
import { AnexosEditor, fonteImagem } from '../Anexos';
import { QuadroPagina } from '../QuadroPagina';
import { DesenhoView } from '../Desenho';
import { PickerModal } from '../Pickers';
import { AlertasEditor } from '../AlertasEditor';
import { alertasDosDias, opcoesDias, rotuloDias } from '../../lib/alertasData';
import type { AlertaItem } from '../../store/types';
import { Btn, Chip, Field, Screen, Sheet } from '../ui';
import { C, alpha } from '../theme';
import { Corpo, dataLonga } from './comum';
import { diasAte } from '../../modulos/util';
import { addMonths, horaTela, parseYmd, today, uid, ymd } from '../../lib/date';
import { t } from '../../lib/i18n';
import { useBotaoNovo } from '../../lib/botaoNovo';
import { Image } from 'react-native';

/** "1 ano", "6 meses", "10 anos", "45 dias": quanto tempo entre escrever e abrir. */
export function tempoDaCarta(escrita: string, abre: string) {
  const meses = (parseYmd(abre).getFullYear() - parseYmd(escrita).getFullYear()) * 12 + parseYmd(abre).getMonth() - parseYmd(escrita).getMonth();
  if (meses >= 12 && meses % 12 === 0) return meses === 12 ? t('1 ano') : t('{n} anos', { n: meses / 12 });
  if (meses >= 1) return meses === 1 ? t('1 mês') : t('{n} meses', { n: meses });
  const d = Math.max(1, Math.round((parseYmd(abre).getTime() - parseYmd(escrita).getTime()) / 864e5));
  return t('{n} dias', { n: d });
}

export function Cartas({ cor, abrirId, onBack }: { cor: string; abrirId?: string | null; onBack: () => void }) {
  useDiario();
  const dp = useDoc('diariop');
  const { nome } = useAuth();
  const primeiro = (nome || '').trim().split(/\s+/)[0] || t('Você');
  const cartas = [...(dp.cartas ?? [])].sort((a, b) => a.abre.localeCompare(b.abre));
  const [nova, setNova] = useState(false);
  useBotaoNovo({ rotulo: t('Escrever uma carta'), fn: () => setNova(true) });   // 11d F
  const [ler1, setLer1] = useState<string | null>(abrirId ?? null);
  const hoje = today();
  const abrir = (id: string) => {
    const c = cartas.find(x => x.id === id); if (!c) return;
    if (c.abre > hoje) return Alert.alert('🔒 ' + t('Carta trancada'), t('Ela abre em {d} ({n} dias). Até lá não dá para reler nem editar.', { d: dataLonga(c.abre), n: diasAte(c.abre) }));
    if (!c.aberta) mudarDiario(d => ({ ...d, cartas: (d.cartas ?? []).map(x => (x.id === id ? { ...x, aberta: hoje } : x)) }));
    setLer1(id);
  };
  const lida = ler1 ? cartas.find(x => x.id === ler1) : null;
  return (
    <Screen title={t('Cartas para o Futuro')} onBack={onBack}>
      <Text style={{ color: C.sub, fontSize: 13 }}>{t('Escreva para você do futuro. A carta fica trancada até o dia de abrir.')}</Text>
      {cartas.map(c => {
        const tranc = c.abre > hoje;
        return (
          <Pressable key={c.id} onPress={() => abrir(c.id)} style={{ backgroundColor: tranc ? C.card : alpha(cor, 0.12), borderRadius: 16, padding: 14, gap: 4, borderWidth: 1, borderColor: tranc ? C.line : cor }}>
            <Text style={{ color: C.text, fontWeight: '800', fontSize: 15 }}>{tranc ? '🔒' : c.aberta ? '📬' : '💌'} {t('Para {nome} de {tempo} depois', { nome: c.de, tempo: tempoDaCarta(c.escrita, c.abre) })}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }}>{t('Escrita em {e} · abre em {a}', { e: dataLonga(c.escrita), a: dataLonga(c.abre) })}</Text>
            {tranc ? <Text style={{ color: cor, fontWeight: '700', fontSize: 12 }}>⏳ {t('abre em {n} dias', { n: diasAte(c.abre) })}</Text>
              : <Text style={{ color: cor, fontWeight: '700', fontSize: 12 }}>{c.aberta ? t('Aberta em {d}', { d: dataLonga(c.aberta) }) : t('Chegou! Toque para abrir 💌')}</Text>}
          </Pressable>
        );
      })}
      {!cartas.length && <Text style={{ color: C.sub }}>{t('Nenhuma carta ainda.')}</Text>}
      <Pressable onPress={() => setNova(true)} style={{ backgroundColor: cor, borderRadius: 14, padding: 14, alignItems: 'center', marginTop: 6 }}>
        <Text style={{ color: '#fff', fontWeight: '800' }}>+ {t('Escrever uma carta')}</Text>
      </Pressable>
      <View style={{ height: 60 }} />
      <NovaCarta visivel={nova} cor={cor} nome={primeiro} onFechar={() => setNova(false)} />
      <Sheet visible={!!lida} onClose={() => setLer1(null)} title={lida ? t('Carta de {d}', { d: dataLonga(lida.escrita) }) : ''}>
        {!!lida && <LerCarta id={lida.id} cor={cor} />}
      </Sheet>
    </Screen>
  );
}

/** Carta aberta: só leitura. */
function LerCarta({ id, cor }: { id: string; cor: string }) {
  const c = ler<Carta | null>('carta:' + id, null);
  if (!c) return <Corpo><Text style={{ color: C.sub }}>{t('O conteúdo desta carta não foi encontrado.')}</Text></Corpo>;
  return (
    <Corpo>
      {!!c.quadro && <DesenhoView tracos={c.quadro.tracos} alt={c.quadro.alt} larg={c.quadro.larg} quadro={c.quadro} />}
      {(c.fotos ?? []).map((f, i) => { const s = fonteImagem(f); return s ? <Image key={i} source={{ uri: s }} style={{ width: '100%', height: 240, borderRadius: 12 }} resizeMode="contain" /> : null; })}
      {(c.videos ?? []).map(v => (
        <Pressable key={v.id} onPress={() => semBloqueio(() => Linking.openURL(v.url)).catch(() => {})}><Text style={{ color: cor, fontWeight: '700' }}>▶️ {v.titulo || v.url}</Text></Pressable>
      ))}
      <Text style={{ color: C.mute, fontSize: 12 }}>{t('Cartas abertas podem ser lidas, mas não editadas.')}</Text>
    </Corpo>
  );
}

const minuscula = (x: string) => x.charAt(0).toLocaleLowerCase() + x.slice(1);
/** texto 12: a confirmação diz os alertas que a pessoa escolheu ("Sem alerta": não fala de aviso). */
function avisoGuardada(abre: string, alertas: AlertaItem[]) {
  const d = dataLonga(abre);
  const l = alertas.filter(a => a.tipo === 'hora' && a.como !== 'nada' && !!a.hora && /^\d{2}:\d{2}$/.test(a.hora))
    .sort((x, y) => (y.dias ?? 0) - (x.dias ?? 0) || String(x.hora).localeCompare(String(y.hora)));
  if (!l.length) return t('Ela abre em {d}.', { d });
  if (l.length === 1 && !l[0].dias) return t('Ela abre em {d}. Você vai receber um aviso nesse dia.', { d });
  const quando = l.map(a => t('{quando} às {hora}', { quando: minuscula(rotuloDias(a.dias ?? 0)), hora: horaTela(a.hora!) })).join(', ');
  return l.length === 1 ? t('Ela abre em {d}. Você vai receber um aviso: {quando}.', { d, quando }) : t('Ela abre em {d}. Você vai receber avisos: {quando}.', { d, quando });
}

function NovaCarta({ visivel, cor, nome, onFechar }: { visivel: boolean; cor: string; nome: string; onFechar: () => void }) {
  const [c, setC] = useState<Carta>({ id: uid(), quadro: null, fotos: [], videos: [] });
  const [abre, setAbre] = useState(ymd(addMonths(new Date(), 12)));
  const [atalho, setAtalho] = useState<number | null>(12);
  const [alertas, setAlertas] = useState<AlertaItem[]>(() => alertasDosDias([], true));   // texto 10 (A4): padrão às 9h do dia
  const [data, setData] = useState(false);
  const [vid, setVid] = useState('');
  const [aberto, setAberto] = useState(visivel);
  if (visivel !== aberto) { setAberto(visivel); if (visivel) { setC({ id: uid(), quadro: null, fotos: [], videos: [] }); setAbre(ymd(addMonths(new Date(), 12))); setAtalho(12); setAlertas(alertasDosDias([], true)); } }
  const atalhos: [number, string][] = [[6, t('{nome} daqui a 6 meses', { nome })], [12, t('{nome} daqui a 1 ano', { nome })], [120, t('{nome} daqui a 10 anos', { nome })]];
  const salvar = () => {
    const vazia = !c.quadro?.tracos.length && !c.quadro?.textos.some(x => x.texto.trim()) && !c.fotos?.length && !c.videos?.length;
    if (vazia) return Alert.alert(t('A carta está vazia'), t('Escreva alguma coisa antes de guardar.'));
    if (abre <= today()) return Alert.alert(t('Escolha uma data no futuro'));
    Alert.alert('🔒 ' + t('Guardar e trancar?'), t('A carta fica trancada até {d}. Até lá não dá para reler nem editar. Para mudar algo, escreva outra carta.', { d: dataLonga(abre) }), [
      { text: t('Cancelar'), style: 'cancel' },
      { text: t('Guardar'), onPress: () => {
        gravar('carta:' + c.id, c);
        // o dia de abrir fica marcado no My Planner, só com o título; tocar leva à carta (pede a senha do diário)
        const tk = newTask({ title: t('Abrir carta para o futuro'), emoji: '💌', emojiManual: true, start: abre, area: 'diario', energy: 0,
          alerts: { mode: 'none', before: [] }, link: { aba: 'diario', id: 'carta:' + c.id, label: t('Carta para o futuro') } });
        saveTask(tk);
        mudarDiario(d => ({ ...d, cartas: [...(d.cartas ?? []), { id: c.id, abre, escrita: today(), de: nome, tarefa: tk.id, alertas }] }));
        onFechar();
        Alert.alert('💌 ' + t('Carta guardada'), avisoGuardada(abre, alertas));
      } }]);
  };
  return (
    <Sheet visible={visivel} onClose={() => Alert.alert(t('Sair sem guardar?'), t('O que você escreveu nesta carta vai se perder.'), [{ text: t('Continuar escrevendo'), style: 'cancel' }, { text: t('Sair'), style: 'destructive', onPress: onFechar }])}
      title={t('Nova carta')} right={<Pressable onPress={salvar}><Text style={{ color: cor, fontWeight: '800' }}>{t('Guardar')}</Text></Pressable>}>
      <Corpo>
        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>{t('QUANDO ELA ABRE')}</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
          {atalhos.map(([m, l]) => <Chip key={m} small cor={cor} on={atalho === m} label={l} onPress={() => { setAtalho(m); setAbre(ymd(addMonths(new Date(), m))); }} />)}
          <Chip small cor={cor} on={atalho === null} label={'📅 ' + t('Outra data')} onPress={() => setData(true)} />
        </View>
        <Pressable onPress={() => setData(true)}><Text style={{ color: C.text }}>{t('Abre em {d}', { d: dataLonga(abre) })} ✏️</Text></Pressable>
        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>🔔 {t('ALERTAS')}</Text>
        <AlertasEditor itens={alertas} onChange={setAlertas} temHorario={false} cor={cor} aba="diario" dias={opcoesDias()} />
        <QuadroPagina valor={c.quadro} onChange={q => setC(x => ({ ...x, quadro: q }))} cor={cor} aba="diario" altura={420} encherTela nome={t('Carta para o futuro')} />
        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>{t('FOTOS')}</Text>
        <AnexosEditor lista={c.fotos ?? []} cor={cor} onChange={l => setC(x => ({ ...x, fotos: l }))} aviso={false} />
        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>{t('LINKS DE VÍDEO')}</Text>
        {(c.videos ?? []).map(v => <Text key={v.id} style={{ color: cor }} numberOfLines={1}>▶️ {v.url}</Text>)}
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <Field value={vid} onChangeText={v => setVid(v.trim())} placeholder="https://" autoCapitalize="none" autoCorrect={false} keyboardType="url" style={{ flex: 1 }} />
          <Chip cor={cor} on label={t('Adicionar')} onPress={() => { if (/^https?:\/\/\S+/i.test(vid)) { setC(x => ({ ...x, videos: [...(x.videos ?? []), { id: uid(), titulo: '', url: vid }] })); setVid(''); } }} />
        </View>
        <Btn cor={cor} label={'🔒 ' + t('Guardar e trancar')} onPress={salvar} />
      </Corpo>
      {data && <PickerModal mode="date" cor={cor} value={parseYmd(abre)} onClose={() => setData(false)} onPick={d => { setAbre(ymd(d)); setAtalho(null); }} />}
    </Sheet>
  );
}

/** Carta que já pode ser aberta (para o aviso e para o My Planner). */
export const cartasParaAbrir = () => (getDoc('diariop').cartas ?? []).filter(c => c.abre <= today() && !c.aberta);
